import { MoMBreakdownChart } from "components/BooksHomePage/BusinessOverview/MoMBreakdownChart";
import { Spinner } from "components/DesignSystem/Loader/loader";
import Async from "components/DesignSystem/AsyncComponents/Async";
import {
  EXPENSE_CATEGORY_IDENTIFIER,
  REVENUE_CATEGORY_IDENTIFIER,
} from "constants/categoryIdentifier";
import { YYYY_MM_DD } from "constants/date";
import dayjs from "dayjs";
import { useChartOfAccounts } from "hooks/useChartOfAccounts";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  ForecastQueryParams,
  useGetMoMBreakdownQuery,
} from "store/apis/financialMetrics";
import { useGetBookkeepingSettingsQuery } from "store/apis/booksLockDate";
import { useGetCategoryByIdentifierQuery } from "store/apis/chartOfAccounts";
import { setLedgerFilter } from "store/slices/generalLedger";
import { flattenTypes } from "utils/flattenCOA";
import {
  BarForecastMeta,
  ForecastLegend,
  GenericBarChartData,
  buildForecastMetaByDate,
} from "components/BooksHomePage/ChartComponents";
import { CashBalanceIcon } from "components/icons/CashBalanceIcon";

type MoMBreakdownProps = {
  startDate: string;
  endDate: string;
  reportType: "REVENUE" | "EXPENSE";
  title: string;
} & ForecastQueryParams;

export const MoMBreakdown = ({
  startDate,
  endDate,
  reportType,
  title,
  assumption_set_id,
  forecast_start_date,
  forecast_end_date,
}: MoMBreakdownProps) => {
  const entityId = useCurrentEntityId();
  const { uuid: groupId } = useCurrentGroupContext();
  const dispatch = useDispatch();
  const history = useHistory();
  const { link } = useConstructInternalLink();

  const identifier =
    reportType === "REVENUE"
      ? REVENUE_CATEGORY_IDENTIFIER
      : EXPENSE_CATEGORY_IDENTIFIER;

  const { data: categoryData } = useGetCategoryByIdentifierQuery(
    { groupId, entityId, identifiers: [identifier] },
    { skip: !groupId || !entityId }
  );

  const rootUUID = Array.isArray(categoryData)
    ? categoryData?.map((c) => c.uuid).join(",")
    : "";

  const { chartOfAccounts } = useChartOfAccounts({
    root_category_id: rootUUID,
  });

  const { data: bookkeepingSettings } = useGetBookkeepingSettingsQuery(
    { entityId },
    { skip: !entityId }
  );

  const { data, isLoading, isSuccess, isError } = useGetMoMBreakdownQuery(
    {
      entityId,
      start_date: startDate,
      end_date: endDate,
      report_type: reportType,
      assumption_set_id,
      forecast_start_date,
      forecast_end_date,
    },
    { skip: !entityId }
  );

  const handleAmountClick = useCallback(
    (date: string) => {
      const categoryIds = flattenTypes({ accounts: chartOfAccounts }).map(
        (t) => t.uuid
      );
      if (!categoryIds.length) return;

      const monthStart = dayjs(date).startOf("month").format(YYYY_MM_DD);
      const monthEnd = dayjs(date).endOf("month").format(YYYY_MM_DD);

      dispatch(
        setLedgerFilter({
          categoryIds,
          ...(bookkeepingSettings?.accounting_method && {
            accountingMethod: bookkeepingSettings.accounting_method,
          }),
        })
      );

      history.push(
        link(`/books/categories`, {
          moreQuery: { startDate: monthStart, endDate: monthEnd },
        })
      );
    },
    [chartOfAccounts, bookkeepingSettings, dispatch, history, link]
  );

  const breakdownData = Array.isArray(data) ? data : [];

  const chartData: GenericBarChartData[] = breakdownData.map((item) => ({
    date: item.month,
    bar1: item.amount,
    bar2: 0,
  }));

  const getMoMForecastFields = (
    item: (typeof breakdownData)[number]
  ): BarForecastMeta => ({
    isForecast: item.is_forecast,
    hasOverlapActual: item.has_overlap_actual,
    actualBar1: item.actual_amount ?? undefined,
  });

  const { forecastMetaByDate, hasForecastData } = buildForecastMetaByDate(
    breakdownData,
    (item) => item.month,
    getMoMForecastFields
  );

  return (
    <div className="t-flex t-flex-col t-gap-4 t-bg-surface t-border t-border-solid t-border-neutral-10 t-rounded-lg t-p-5 sm:t-px-3 t-h-full">
      <div className="t-pb-4 t-border-b t-border-solid t-border-neutral-10 t-border-x-0 t-border-t-0">
        <span className="t-text-subtitle-lg t-text-text-black">{title}</span>
      </div>
      {hasForecastData && (
        <div className="t-flex t-gap-4 t-items-center">
          <ForecastLegend show={hasForecastData} />
        </div>
      )}
      <Async.Root
        isLoading={isLoading}
        isSuccess={isSuccess}
        isEmpty={!breakdownData.length}
        isError={isError}
        customLoader={
          <div className="t-flex t-flex-col t-justify-center t-items-center t-h-full t-gap-3">
            <Spinner />
          </div>
        }
      >
        <Async.Empty>
          <div className="t-flex t-flex-col t-justify-center t-items-center t-h-full t-text-text-30 t-gap-1 sm:t-text-center sm:t-px-4">
            <CashBalanceIcon />
            <span className="t-text-body-sm">No transactions categorised</span>
            <div className="t-text-caption">
              {reportType === "REVENUE"
                ? "Add transactions to see revenue breakdown"
                : "Add transactions to see expense breakdown"}
            </div>
          </div>
        </Async.Empty>
        <Async.ErrorHandler>
          <div className="t-flex t-justify-center t-items-center t-h-full t-text-red-50">
            Something went wrong
          </div>
        </Async.ErrorHandler>
        <Async.Success>
          <MoMBreakdownChart
            data={chartData}
            reportType={reportType}
            onAmountClick={handleAmountClick}
            forecastMetaByDate={forecastMetaByDate}
          />
        </Async.Success>
      </Async.Root>
    </div>
  );
};
